export interface ImmediateVoiceAction {
  type: "navigate" | "navigate_to_tab" | "open_form";
  parameters: Record<string, string>;
  spokenResponse: string;
}

const VOICE_COPY = {
  en: {
    openingHome: "Opening home now.",
    openingJobs: "Opening jobs now.",
    openingLeads: "Opening leads now.",
    openingCalendar: "Opening calendar now.",
    openingMeasurements: "Opening measurements now.",
    openingSalesHub: "Opening sales hub now.",
    openingEmail: "Opening email now.",
    openingSettings: "Opening settings now.",
    openingPortfolio: "Opening portfolio now.",
    openingCatalog: "Opening catalog now.",
    openingStormWatch: "Opening storm watch now.",
    openingCanvassing: "Opening canvassing now.",
    openingForms: "Opening forms and templates now.",
    openingOverview: "Opening overview now.",
    openingDamageHub: "Opening damage hub now.",
    openingDocuments: "Opening documents now.",
    openingEstimates: "Opening estimates now.",
    openingSupplements: "Opening supplements now.",
    openingProposals: "Opening proposals now.",
    openingTasks: "Opening tasks now.",
    openingPayments: "Opening payments now.",
    openingCommunications: "Opening communications now.",
    openingTimeline: "Opening timeline now.",
    openingTaskForm: "Opening task form now.",
    openingNoteForm: "Opening note form now.",
    openingProposalForm: "Opening proposal form now.",
    openingPhotoUpload: "Opening photo upload now.",
    openingDocumentUpload: "Opening document upload now.",
    openingJobEditor: "Opening job editor now.",
  },
  ru: {
    openingHome: "Открываю главную.",
    openingJobs: "Открываю список работ.",
    openingLeads: "Открываю лиды.",
    openingCalendar: "Открываю календарь.",
    openingMeasurements: "Открываю замеры.",
    openingSalesHub: "Открываю sales hub.",
    openingEmail: "Открываю почту.",
    openingSettings: "Открываю настройки.",
    openingPortfolio: "Открываю портфолио.",
    openingCatalog: "Открываю каталог.",
    openingStormWatch: "Открываю storm watch.",
    openingCanvassing: "Открываю canvassing.",
    openingForms: "Открываю формы и шаблоны.",
    openingOverview: "Открываю обзор.",
    openingDamageHub: "Открываю damage hub.",
    openingDocuments: "Открываю документы.",
    openingEstimates: "Открываю сметы.",
    openingSupplements: "Открываю supplements.",
    openingProposals: "Открываю предложения.",
    openingTasks: "Открываю задачи.",
    openingPayments: "Открываю платежи.",
    openingCommunications: "Открываю сообщения.",
    openingTimeline: "Открываю timeline.",
    openingTaskForm: "Открываю форму задачи.",
    openingNoteForm: "Открываю форму заметки.",
    openingProposalForm: "Открываю форму предложения.",
    openingPhotoUpload: "Открываю загрузку фото.",
    openingDocumentUpload: "Открываю загрузку документа.",
    openingJobEditor: "Открываю редактор работы.",
  },
} as const;

const ACTION_INTENT_REGEX = /\b(open|go to|goto|show|take me to|switch to|launch|head to|bring up|pull up)\b/i;
const CREATE_INTENT_REGEX = /\b(create|add|new|upload|edit)\b/i;

function normalize(input: string) {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9\s/-]/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

function isJobDetailPath(pathname: string) {
  return /^\/jobs\/[^/]+$/.test(pathname);
}

function isLeadDetailPath(pathname: string) {
  return /^\/leads\/[^/]+$/.test(pathname);
}

export function resolveImmediateVoiceAction(
  userInput: string,
  pathname: string,
  lang: "en" | "ru" = "en",
): ImmediateVoiceAction | null {
  const text = normalize(userInput);
  const hasActionIntent = ACTION_INTENT_REGEX.test(text);
  const hasCreateIntent = CREATE_INTENT_REGEX.test(text);
  const copy = VOICE_COPY[lang];

  const topLevelRoutes = [
    { match: /\b(home|dashboard)\b/, path: "/", spokenResponse: copy.openingHome },
    { match: /\bjobs\b|\bjob list\b/, path: "/jobs", spokenResponse: copy.openingJobs },
    { match: /\bleads\b|\blead list\b/, path: "/leads", spokenResponse: copy.openingLeads },
    { match: /\bcalendar\b/, path: "/calendar", spokenResponse: copy.openingCalendar },
    { match: /\bmeasurements\b|\bmeasurement page\b/, path: "/measurements", spokenResponse: copy.openingMeasurements },
    { match: /\bsales hub\b|\bsales command center\b/, path: "/sales-hub", spokenResponse: copy.openingSalesHub },
    { match: /\bemail\b|\binbox\b/, path: "/email", spokenResponse: copy.openingEmail },
    { match: /\bsettings\b/, path: "/settings", spokenResponse: copy.openingSettings },
    { match: /\bportfolio\b/, path: "/portfolio", spokenResponse: copy.openingPortfolio },
    { match: /\bcatalog\b/, path: "/catalog", spokenResponse: copy.openingCatalog },
    { match: /\bstorm watch\b/, path: "/storm-watch", spokenResponse: copy.openingStormWatch },
    { match: /\bcanvassing\b/, path: "/canvassing", spokenResponse: copy.openingCanvassing },
    { match: /\bforms\b|\btemplates\b/, path: "/templates", spokenResponse: copy.openingForms },
  ];

  for (const route of topLevelRoutes) {
    if ((hasActionIntent || text === route.path.slice(1) || text === "home" || text === "dashboard") && route.match.test(text)) {
      return {
        type: "navigate",
        parameters: { path: route.path },
        spokenResponse: route.spokenResponse,
      };
    }
  }

  if (isJobDetailPath(pathname) && hasActionIntent) {
    const jobTabs = [
      { match: /\boverview\b/, tab: "Overview", spokenResponse: copy.openingOverview },
      { match: /\bphotos\b|\bphoto\b|\bdamage hub\b/, tab: "Damage Hub", spokenResponse: copy.openingDamageHub },
      { match: /\bdocuments\b|\bdocs\b/, tab: "Documents", spokenResponse: copy.openingDocuments },
      { match: /\bestimates\b|\bestimate\b/, tab: "Estimates", spokenResponse: copy.openingEstimates },
      { match: /\bsupplements\b|\bsupplement\b/, tab: "Supplements", spokenResponse: copy.openingSupplements },
      { match: /\bproposals\b|\bproposal\b/, tab: "Proposals", spokenResponse: copy.openingProposals },
      { match: /\btasks\b|\btask\b/, tab: "Tasks", spokenResponse: copy.openingTasks },
      { match: /\bpayments\b|\bpayment\b/, tab: "Payments", spokenResponse: copy.openingPayments },
      { match: /\bcommunications\b|\bmessages\b|\bmessage\b/, tab: "Communications", spokenResponse: copy.openingCommunications },
      { match: /\btimeline\b/, tab: "Timeline", spokenResponse: copy.openingTimeline },
    ];

    for (const tab of jobTabs) {
      if (tab.match.test(text)) {
        return {
          type: "navigate_to_tab",
          parameters: { tab: tab.tab },
          spokenResponse: tab.spokenResponse,
        };
      }
    }
  }

  if ((isJobDetailPath(pathname) || isLeadDetailPath(pathname)) && hasCreateIntent) {
    const forms = [
      { match: /\btask\b/, formType: "create_task", spokenResponse: copy.openingTaskForm },
      { match: /\bnote\b/, formType: "add_note", spokenResponse: copy.openingNoteForm },
      { match: /\bproposal\b/, formType: "create_proposal", spokenResponse: copy.openingProposalForm },
      { match: /\bphoto\b/, formType: "upload_photo", spokenResponse: copy.openingPhotoUpload },
      { match: /\bdocument\b/, formType: "upload_document", spokenResponse: copy.openingDocumentUpload },
      { match: /\bedit\b.*\bjob\b/, formType: "edit_job", spokenResponse: copy.openingJobEditor },
    ];

    for (const form of forms) {
      if (form.match.test(text)) {
        return {
          type: "open_form",
          parameters: { form_type: form.formType },
          spokenResponse: form.spokenResponse,
        };
      }
    }
  }

  return null;
}